import type { Node } from '@craftjs/core';
import {
  FormConsentField as SharedFormConsentField,
  type FormConsentFieldProps,
  formConsentFieldDefaultProps,
} from '@builder4/shared/components/Form';
import { FORM_CONSENT_FIELD_NODE_NAME, FORM_TEXT_FIELD_NODE_NAME } from '@/utils/constants';
import { InspectorForm } from '@/components/inspector/InspectorForm/InspectorForm';

type NodeQueryHelper = (id: string) => {
  get(): Node | null;
  childNodes(): string[];
};

const EditorFormConsentField = (props: Partial<FormConsentFieldProps>) => {
  return <SharedFormConsentField {...props} />;
};

EditorFormConsentField.craft = {
  ...SharedFormConsentField.craft,
  displayName: FORM_CONSENT_FIELD_NODE_NAME,
  props: formConsentFieldDefaultProps,
  related: {
    toolbar: InspectorForm,
  },
  rules: {
    canDrag: (node: Node, helpers: NodeQueryHelper) => {
      if (node.data.custom?.isLocked) {
        return false;
      }
      const parentId = node.data.parent;
      if (!parentId) return false;
      const parent = helpers(parentId).get();
      if (!parent) return false;
      // Check if there are any text fields remaining (consent fields are optional)
      const siblingTextFieldIds = parent.data.nodes.filter((childId: string) => {
        const child = helpers(childId).get();
        return child?.data.displayName === FORM_TEXT_FIELD_NODE_NAME;
      });
      // Allow dragging as long as there's at least one text field
      return siblingTextFieldIds.length > 0;
    },
    canMoveOut: (outgoingNodes: Node[], self: Node, helpers: NodeQueryHelper) => {
      const parentId = self.data.parent;
      if (!parentId) return false;
      const parent = helpers(parentId).get();
      if (!parent) return false;
      // Check if there will be any text fields remaining
      const textFieldIds = parent.data.nodes.filter((childId: string) => {
        const child = helpers(childId).get();
        return child?.data.displayName === FORM_TEXT_FIELD_NODE_NAME;
      });
      const movingIds = new Set(outgoingNodes.map((n) => n.id));
      const remaining = textFieldIds.filter((fieldId: string) => !movingIds.has(fieldId));
      // Allow moving out as long as there's at least one text field remaining
      return remaining.length >= 1;
    },
    canMoveIn: () => false, // Fields cannot accept children
  },
};

export default EditorFormConsentField;
